import { Stack, Typography, Avatar, Button, Box, Divider, IconButton } from '@mui/material';
import { Fragment, useState } from 'react';
import { uniqueId } from 'lodash';
import { getInitials } from '@/utils/getInitials';
import { useNotesContext } from '../../hooks/useNotesContext';
import { TNoteMessage } from '../../types';
import { renderHighlightedTextWithHTML } from '../../utils';
import WaveSurferPlayer from '@/components/common/AudioPlayer/WaveSurferPlayer';
import { formatDate } from '../note';
import { Gallery } from '@/components/common/Gallery/Gallery';
import { NoteFormat, NoteIntegration, NoteType } from '../../enums';
import { BazanDetails } from '../note-details/SAPDetails';
import { useMediaUrls } from './hooks/use-media-urls';
import { useNotesStore } from '../../stores/notes.store';
import { NoteDetails } from '../note-details';

type Props = {
  message: TNoteMessage;
  form: React.ReactNode;
  isCompleted: boolean;
};

export function NoteMainMessage({ form, message, isCompleted }: Props) {
  const { noteToEdit, setCurrentAction, userId, isGuest, isRtl, collaborators, integration } =
    useNotesContext();
  const selectedNote = useNotesStore((state) => state.selectedNote);
  const [isExpanded, setIsExpanded] = useState(false);
  const repliesCount = selectedNote?.messages?.length ? selectedNote?.messages?.length - 1 : 0;

  const isModified =
    new Date(message?.createdAt).getTime() !== new Date(message?.updatedAt).getTime();
  const messageAuthor = collaborators?.find((c) => c.id === message?.userId);

  const stripHtmlTags = (str: string) => {
    return str.replace(/<[^>]*>/g, '');
  };

  const descriptionText = stripHtmlTags(message?.description || '');
  const isLongText = descriptionText?.length > 100;
  const { audioMessages, imageMessages } = useMediaUrls(message?.mediaUrls);

  return (
    <Stack key={message?.id} direction="row" flexWrap="wrap" width="100%" mt="18px">
      <Stack gap="10px" px="18px" pb="14px" width="100%" sx={{ background: 'white', pt: '10px' }}>
        {selectedNote?.title && (
          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
            <Typography
              variant="h6"
              width="100%"
              sx={{
                textAlign: 'left',
                fontSize: '18px',
                lineHeight: '28px',
                color: '#000000',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: '1 0 0',
              }}
            >
              {selectedNote?.title}
            </Typography>
            {message?.userId === userId && (
              <IconButton
                onClick={() => setCurrentAction({ type: 'change-status', note: selectedNote })}
                id={`message-button-${message?.id}`}
                // aria-controls={isMenuOpen ? `message-menu-${message?.id}` : undefined}
                aria-haspopup="true"
                // aria-expanded={isMenuOpen ? 'true' : undefined}
              >
                {!isCompleted ? (
                  <i className="fa-light fa-check" style={{ color: '#12B76A', fontSize: '24px' }} />
                ) : (
                  <i
                    className="fa-light fa-arrow-rotate-left"
                    style={{ color: '#12B76A', fontSize: '24px' }}
                  />
                )}
              </IconButton>
            )}
          </Stack>
        )}
        <Stack width="100%" direction="row" alignItems="center" height="24px" mb="10px">
          <Stack direction="row" gap={2} alignItems="center">
            <Avatar
              sx={{
                height: '22px',
                width: '22px',
                img: { objectFit: 'contain' },
                bgcolor: userId === messageAuthor?.id ? '#2055ff' : 'white',
                color: userId === messageAuthor?.id ? 'white' : 'black',
                border: '1px solid #D1D1D1',
                fontSize: '12px',
              }}
            >
              {getInitials(messageAuthor?.name || 'Guest')}
            </Avatar>
            <Typography
              sx={{
                fontSize: '14px',
                color: '#101828',
              }}
            >
              {messageAuthor?.name || 'Guest'}
            </Typography>
          </Stack>
          <Divider orientation="vertical" sx={{ marginX: '5px', height: '15px' }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: '14px',
              color: '#101828',
            }}
          >
            {formatDate(message?.createdAt)}
          </Typography>
          {isModified && (
            <>
              <Divider orientation="vertical" sx={{ marginX: '5px', height: '15px' }} />
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#101828',
                }}
              >
                Edited
              </Typography>
            </>
          )}
        </Stack>
        {message?.description && (
          <Box sx={{ display: 'inline' }}>
            <Typography
              width="100%"
              sx={{
                maxWidth: '400px',
                display: 'inline',
                textAlign: isRtl ? 'right' : 'left',
                color: '#000000',
                fontSize: '14px',
                lineHeight: '20px',
                wordBreak: 'break-all',
                whiteSpace: 'normal',
              }}
              dangerouslySetInnerHTML={
                isExpanded || !isLongText
                  ? renderHighlightedTextWithHTML(
                      message?.description === 'null' ? '<span></span>' : message?.description,
                      collaborators,
                    )
                  : renderHighlightedTextWithHTML(
                      `${message?.description.slice(0, 100)}...`,
                      collaborators,
                    )
              }
            />
            {isLongText && (
              <Button
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{
                  display: 'inline',
                  textTransform: 'none',
                  padding: 0,
                  minHeight: 0,
                  minWidth: 0,
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: 'black',
                  '&:hover': {
                    color: 'black',
                    textDecoration: 'underline',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {isExpanded ? '...Read less' : 'Read more'}
              </Button>
            )}
          </Box>
        )}
        {!!audioMessages?.length || !!imageMessages?.length ? (
          <Divider sx={{ width: '100%' }} />
        ) : null}
        {!!audioMessages?.length &&
          audioMessages.map((url) => {
            return (
              <Fragment key={uniqueId()}>
                <WaveSurferPlayer
                  url={`${process.env.NEXT_PUBLIC_CDN}/${url}`}
                  waveColor="#D0D5DD"
                  progressColor="#2055FF"
                  barWidth={2}
                  barGap={2}
                  barRadius={4}
                  barHeight={10}
                  wavefHeight={33}
                  playPauseButtonSx={{ color: '#2055FF', padding: 0, height: '30px' }}
                  containerSx={{
                    padding: '10px',
                    height: '70px',
                    maxHeight: '70px',
                    width: '100%',
                    gap: '6px',
                    background: '#F9FAFB',
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                  }}
                />
              </Fragment>
            );
          })}
        {!!imageMessages?.length && (
          <Box width="320px" margin="0 auto">
            <Gallery
              items={imageMessages}
              settings={{
                fullScreen: false,
                imageHeight: 252,
                dotsColor: '#D0D5DD',
                thumbnailIconBackground: '#F4F5F2',
                thumbnailBorderColor: '#D1D1D1',
                thumbnailSize: 70,
                fullscreenByClick: true,
                alternativeArrows: false,
                thumbnails: imageMessages.length > 1,
                dots: imageMessages.length > 1,
                isNotesGallery: true,
              }}
            />
          </Box>
        )}
        {integration !== NoteIntegration.Bazan &&
        selectedNote &&
        (selectedNote?.type === NoteType.Fault || selectedNote?.type === NoteType.PreventiveMaintenance) ? (
          <>
            <Divider sx={{ width: '100%' }} />
            <NoteDetails note={selectedNote} />
          </>
        ) : null}
        {selectedNote?.format === NoteFormat.Bazan && integration === NoteIntegration.Bazan && (
          <Stack>
            <BazanDetails SAPFields={selectedNote.SAPFields} />
          </Stack>
        )}
      </Stack>
      {!!noteToEdit && message.id === noteToEdit?.message?.id && userId && !isGuest && (
        <Stack width="100%" p="18px" sx={{ background: '#F1F1F1' }}>
          {form}
        </Stack>
      )}
      <Box sx={{ background: 'transparent', padding: '18px', paddingBottom: '0', width: '100%' }}>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >{`${repliesCount} ${repliesCount === 1 ? 'Reply' : 'Replies'}`}</Typography>
      </Box>
    </Stack>
  );
}
